import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faExchangeAlt, faMoneyBillWave, faCreditCard, 
    faMoneyCheckAlt, faUniversity, faQuestionCircle,
    faArrowDown, faArrowUp
} from '@fortawesome/free-solid-svg-icons';

const FacturaCard = ({ factura, facturasSeleccionadas, toggleSeleccionFactura }) => {
    
    // Función para obtener el icono según la forma de pago
    const getFormaPagoIcon = (formaPagoStr) => {
        if (!formaPagoStr) return faQuestionCircle;
        
        const clave = formaPagoStr.split('-')[0].trim();
        
        switch (clave) {
            case '01': // Efectivo
                return faMoneyBillWave;
            case '02': // Cheque nominativo
                return faMoneyCheckAlt;
            case '03': // Transferencia electrónica de fondos
                return faExchangeAlt;
            case '04': // Tarjeta de crédito
            case '28': // Tarjeta de débito
                return faCreditCard;
            case '99': // Por definir
                return faQuestionCircle;
            default:
                return faUniversity;
        }
    };

    const getTipoComprobanteConfig = (tipo) => {
        const configs = {
            'I': { label: 'I - Ingreso', variant: 'success', icon: faArrowDown },
            'E': { label: 'E - Egreso', variant: 'danger', icon: faArrowUp }
        };
        // faArrowDown y faArrowUp we'll import them locally or just use strings if we didn't import them.
        // Wait, they were imported in ConciliarFacturas.js. We need to import them here.
        return configs[tipo] || { label: 'Desconocido', variant: 'secondary', icon: faMoneyBillWave };
    };

    const tipoConfig = getTipoComprobanteConfig(factura.tipo);

    return (
        <Card
            className={`shadow-sm hover-shadow transition-base ${facturasSeleccionadas.some(f => f.id === factura.id) ? 'border-primary border-2 bg-soft-primary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSeleccionFactura(factura)}
        >
            <Card.Body className="p-3">
                <Row className="align-items-center mb-2">
                    <Col>
                        <div className="d-flex align-items-center">
                            {facturasSeleccionadas.some(f => f.id === factura.id) && (
                                <i className="fas fa-check-circle text-primary me-2 fs-3"></i>
                            )}
                            <div
                                className="bg-soft-info rounded p-2 me-2"
                                style={{ width: '40px', height: '40px' }}
                            >
                                <i className="fas fa-file-invoice text-info fs-1"></i>
                            </div>
                            <div>
                                <h6 className="mb-0 text-800">
                                    {factura.serie ? `${factura.serie}-` : ''}{factura.folio}
                                </h6>
                                <div className="text-600 fs--2">
                                    {(() => {
                                        const d = new Date(factura.fecha);
                                        const day = d.getDate().toString().padStart(2, '0');
                                        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                                        const month = months[d.getMonth()];
                                        return `${day}/${month}/${d.getFullYear()}`;
                                    })()}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="auto" className="d-flex flex-column align-items-end gap-1">
                        <Badge bg={tipoConfig.variant} className="d-flex align-items-center gap-1 fs--2">
                            <i className={`fas ${factura.tipo === 'I' ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                            {tipoConfig.label}
                        </Badge>
                        <Badge
                            bg={factura.metodoPago === 'PPD' ? 'danger' : 'light'}
                            text={factura.metodoPago === 'PPD' ? 'white' : 'dark'}
                            className={`d-flex align-items-center justify-content-center gap-1 fs--2 ${factura.metodoPago !== 'PPD' ? 'border' : ''}`}
                        >
                            {factura.metodoPago || 'N/A'}
                        </Badge>
                    </Col>
                </Row>

                <div className="mb-2">
                    <div className="text-700 fw-semi-bold fs--1 mb-1 text-truncate d-flex align-items-center">
                        {factura.rfc !== 'XEXX010101000' && (
                            <img src="/img/bandera-mexico-48.png" width="16" alt="México" className="me-2" />
                        )}
                        <i className="fas fa-building me-2 text-600"></i>
                        <span className="text-truncate">{factura.rfc} - {factura.receptor}</span>
                        {factura.rfc === 'XEXX010101000' && (
                            <i className="fas fa-globe-americas text-info ms-2" title="Extranjero"></i>
                        )}
                    </div>
                    {factura.formaPago && (
                        <div className="text-600 fs--2 mt-1">
                            <FontAwesomeIcon icon={getFormaPagoIcon(factura.formaPago)} className="me-2" />
                            {factura.formaPago}
                        </div>
                    )}
                </div>

                <div className="border-top pt-2">
                    <Row className="align-items-center">
                        <Col>
                            <div className="text-600 fs--2 mb-1">Total</div>
                            <div className="text-primary fw-bold fs-1">
                                ${factura.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                <span className="text-600 fs--1 ms-2">{factura.moneda}</span>
                            </div>
                            {factura.moneda === 'USD' && (
                                <div className="text-600 fs--2">
                                    T.C. ${factura.tipoCambio.toFixed(4)} =
                                    <span className="text-800 fw-semi-bold ms-1">
                                        ${(factura.total * factura.tipoCambio).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                                    </span>
                                </div>
                            )}
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="rounded-pill"
                            >
                                <i className="fas fa-link me-1"></i>
                                Conciliar
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Card.Body>
        </Card>
    );
};



FacturaCard.propTypes = {
    factura: PropTypes.object,
    facturasSeleccionadas: PropTypes.array,
    toggleSeleccionFactura: PropTypes.func
};

export default FacturaCard;
